import {Link} from '@wandb/weave/common/util/links';
import {Button} from '@wandb/weave/components/Button';
import {Icon} from '@wandb/weave/components/Icon';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {TooltipDeprecated} from '@wandb/weave/components/TooltipDeprecated';
import React, {FC, useCallback, useMemo, useState} from 'react';
import {SortFunction} from 'react-table';
import {toast} from 'react-toastify';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Header, Loader, Modal} from 'semantic-ui-react';

import {backendTraceBaseUrl, envIsLocal} from '../../../config';
import * as Generated from '../../../generated/graphql';
import {Invite, WorkflowsAdminType} from '../../../generated/graphql';
import {
  MemberData,
  TeamBaseEntity,
  TeamOrganization,
} from '../../../graphql/teamQuery';
import {Viewer} from '../../../state/viewer/types';
import {propagateErrorsContext} from '../../../util/errors';
import {useRampFlagWeaveAsAService} from '../../../util/rampFeatureFlags';
import {checkIsServiceAccount} from '../../../util/serviceAccounts';
import {capitalizeFirstLetter} from '../../../util/string';
import {useIsGlobalRegistryEnabled} from '../../Registries/common/hooks';
import TypedWBReactTable, {
  DataColumn,
  DataColumns,
  DataRow,
} from '../../WBReactTable/TypedWBReactTable';
import {ModelSeatTypeDropdown} from '../ModelSeatTypeDropdown';
import {RoleOption} from '../TeamOrOrganizationRoleOption';
import {
  getModelsSeatTypeDisplayName,
  getWeaveRoleTypeDisplayName,
  ModelSeatTypeColumnName,
  ModelsSeatType,
  WeaveRoleType,
} from '../util';
import {WeaveRoleTypePill} from '../WeaveRoleTypePill';
import {ModelRegistryAdminSwitch} from './ModelRegistryAdminSwitch';
import {TeamMemberActionMenu} from './TeamMemberActionMenu';
import {TeamMembersRole} from './TeamMembersRole';
import * as S from './TeamMembersTable.styles';
import {getRoleLabel, InviteKey, TeamMemberRole} from './utils';

interface TeamMembersTableProps {
  roleOptions: RoleOption[];
  isViewerAdmin: boolean;
  entity: TeamBaseEntity;
  organization?: TeamOrganization;
  viewer?: Viewer;
  refetch?: () => void;
}

type Row = {
  name: MemberData;
  email: MemberData;
  role: MemberData;
  action: [MemberData, InviteKey];
  model_seat: [MemberData, ModelsSeatType];
  weave_seat: [MemberData, WeaveRoleType];
  modelRegistryAdmin: {
    isModelRegistryAdmin: boolean;
    userId: string;
  };
};

function getSearchOptions(row: Row): string[] {
  const memberData = row.name;
  return [
    memberData.name,
    memberData.username,
    memberData.email,
    getRoleLabel(memberData),
  ];
}
export const TeamMembersTable: FC<TeamMembersTableProps> = ({
  roleOptions,
  isViewerAdmin,
  entity,
  organization,
  refetch,
  viewer,
}) => {
  // The enableGlobalRegistry feature flag is used in this table to decide whether or not the MR admin column should be added
  const {enableGlobalRegistry} = useIsGlobalRegistryEnabled();
  const isWeaveAsAServiceUser =
    useRampFlagWeaveAsAService() ||
    (envIsLocal && backendTraceBaseUrl() !== ''); // Temporary method of determining if org members COULD log a weave run on server;

  const [
    isRemoveSelfRegistryAdminModalOpen,
    setIsRemoveSelfRegistryAdminModalOpen,
  ] = useState(false);

  const getIsMemberCurrentViewer = useCallback(
    (member: MemberData) => {
      return viewer?.username === member.username;
    },
    [viewer]
  );

  const [isSettingRole, setIsSettingRole] = useState(false);

  const isViewerOrgAdmin =
    organization?.members.find(
      mem => mem.username != null && mem.username === viewer?.username
    )?.role === TeamMemberRole.ADMIN;

  function parseInvites(node: Invite) {
    const user = node.toUser;
    const result: InviteKey = {
      id: node.id,
      name: node?.name ?? '',
      createdAt: node?.createdAt ?? new Date(0).toISOString(),
      email: node?.email ?? '',
      username: user?.username ?? '',
      wasCreatedByViewer:
        node?.fromUser?.username === viewer?.username &&
        viewer?.username != null,
    };
    return result;
  }

  const invites: InviteKey[] = entity?.invites?.edges
    ?.map(x => x?.node)
    ?.map(parseInvites);
  // Create map of user email to invite metadata using list of invites
  const latestUniqueInvites = useMemo<Record<string, InviteKey>>(() => {
    return invites?.reduce((set, invite) => {
      if (
        !set[invite.email] ||
        set[invite.email]?.createdAt < invite.createdAt
      ) {
        set[invite.email] = invite;
      }
      return set;
    }, {} as {[key: string]: InviteKey});
  }, [invites]);

  // Check the sort function if you add/remove columns
  const columns: DataColumns<Row> = useMemo(() => {
    const nameColumn: DataColumn<Row> = {
      Header: 'PROFILE',
      accessor: 'name',
      minWidth: 100,
      Cell: cellInfo => {
        const {name, username, photoUrl, pending} = cellInfo.value;
        if (pending) {
          return (
            <div className="flex items-center">
              <img
                src="/default-profile-picture.png"
                alt={`${name}'s profile picture`}
                className="h-36 w-36 rounded-full"
              />
              <div className="overflow-visible pl-5">
                <div className="inline-flex items-center">
                  <i>Invited User</i>
                </div>
              </div>
            </div>
          );
        }
        return (
          <div className="flex items-center">
            <img
              src={photoUrl ?? '/default-profile-picture.png'}
              className="h-36 w-36 rounded-full"
              alt={`${name}'s profile picture`}
            />
            <div className="overflow-visible pl-5">
              <div className="inline-flex items-center">{name}</div>
              <div>
                <Link className="text-teal-550" to={`/${username}`}>
                  @{username}
                </Link>
              </div>
            </div>
          </div>
        );
      },
    };
    const emailColumn: DataColumn<Row> = {
      Header: 'EMAIL',
      accessor: 'email',
      minWidth: 50,
      Cell: cellInfo => {
        const {email} = cellInfo.value;
        return <div className="whitespace-normal break-words">{email}</div>;
      },
    };
    const baseColumns: DataColumns<Row> = [nameColumn, emailColumn];

    const roleColumn: DataColumn<Row> = {
      Header: () => <div className="pl-5">TEAM ROLE</div>,
      accessor: 'role',
      minWidth: 80,
      style: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'left',
      },
      Cell: cellInfo => {
        const {username} = cellInfo.value;
        // org admins can edit their own roles in teams
        const canViewerEditSelfRole =
          isViewerOrgAdmin && username === viewer?.username;
        return (
          <TeamMembersRole
            roleOptions={roleOptions}
            canViewerPerformActions={isViewerAdmin || canViewerEditSelfRole}
            entity={entity}
            member={cellInfo.value}
            refetch={refetch}
            setIsLoading={setIsSettingRole}
          />
        );
      },
    };

    const modelSeatColumn: DataColumn<Row> = {
      Header: () => (
        <TooltipDeprecated
          position="top center"
          className="flex items-center"
          trigger={
            <div className="flex items-center pl-5">
              MODELS SEAT
              <Icon name="info" className="ml-5" />
            </div>
          }>
          <Tailwind>
            <div className="flex items-center">
              A models seat gives you write access to model projects so that you
              can contribute and manage a models project. Organization admins
              can control access on the organization members page.
            </div>
          </Tailwind>
        </TooltipDeprecated>
      ),
      accessor: 'model_seat',
      minWidth: 60,
      sortable: false,
      Cell: cellInfo => {
        const [memberValue, modelsSeat] = cellInfo.value;
        const {pending} = memberValue;
        if (pending) {
          return <div className="flex pl-5 text-moon-450">Invited</div>;
        }
        return (
          <ModelSeatTypeDropdown
            currentModelSeat={modelsSeat}
            username={memberValue.username}
            isDisabled={true}
          />
        );
      },
    };

    const weaveRoleColumn: DataColumn<Row> = {
      Header: 'WEAVE ACCESS',
      accessor: 'weave_seat',
      minWidth: 60,
      sortable: false,
      Cell: cellInfo => {
        const [memberValue, weaveRole] = cellInfo.value;
        const {pending} = memberValue;
        if (pending) {
          return <div className="flex pl-5 text-moon-450">Invited</div>;
        }
        return <WeaveRoleTypePill currentWeaveRole={weaveRole} />;
      },
    };

    baseColumns.push(roleColumn);

    if (isWeaveAsAServiceUser) {
      baseColumns.push(modelSeatColumn);
      baseColumns.push(weaveRoleColumn);
    }
    const modelRegistryAdminColumn: DataColumn<Row> = {
      Header: 'MODEL REGISTRY ADMIN',
      accessor: 'modelRegistryAdmin',
      minWidth: 70,
      style: {
        alignItems: 'center',
        justifyContent: 'center',
      },
      sortMethod: (
        a: {isModelRegistryAdmin: boolean},
        b: {isModelRegistryAdmin: boolean}
      ) => {
        if (a.isModelRegistryAdmin === b.isModelRegistryAdmin) {
          return 0;
        }
        return a.isModelRegistryAdmin ? 1 : -1;
      },
      Cell: cellInfo => {
        const {userId} = cellInfo.value;
        return (
          <ModelRegistryAdminSwitch
            isViewerAdmin={isViewerAdmin}
            entity={entity}
            userId={userId}
            refetch={refetch}
          />
        );
      },
    };
    // The MR admin column should only be added if users are still using the old
    // team-level model registry. If the feature flag for the global registry (new)
    // is enabled, then we don't want to add this column to the table
    if (!enableGlobalRegistry) {
      baseColumns.push(modelRegistryAdminColumn);
    }

    const actionColumn: DataColumn<Row> = {
      Header: '',
      accessor: 'action',
      maxWidth: 80,
      style: {
        display: 'flex',
        justifyContent: 'left',
        alignItems: 'center',
      },
      Cell: cellInfo => {
        const [member, inviteKey] = cellInfo.value;
        return (
          <TeamMemberActionMenu
            member={member}
            organization={organization}
            entity={entity}
            refetch={refetch}
            isViewerAdmin={isViewerAdmin}
            getIsMemberCurrentViewer={getIsMemberCurrentViewer(member)}
            inviteKey={inviteKey}
          />
        );
      },
    };
    return [...baseColumns, actionColumn];
  }, [
    isViewerAdmin,
    isViewerOrgAdmin,
    isWeaveAsAServiceUser,
    entity,
    refetch,
    getIsMemberCurrentViewer,
    organization,
    viewer?.username,
    roleOptions,
    enableGlobalRegistry,
  ]);

  const rows: DataRow<Row>[] = useMemo(
    () =>
      entity.members
        .filter(m => !checkIsServiceAccount(m.accountType))
        .map((member: MemberData) => {
          const {name, username, email, role} = member;
          const nameColText = name ?? '';
          const usernameColText = username ?? '';
          const label = getRoleLabel(member);
          const inviteKey = latestUniqueInvites?.[email];
          const orgMembersModelSeat =
            organization?.members.find(
              mem => mem.username != null && mem.username === username
            )?.modelsSeat ?? 'full';
          let modelsSeat = getModelsSeatTypeDisplayName(
            orgMembersModelSeat as ModelSeatTypeColumnName
          );
          const orgMembersWeaveRole =
            organization?.members.find(
              mem => mem.username != null && mem.username === username
            )?.weaveRole ?? 'full';
          let weaveRole = getWeaveRoleTypeDisplayName(orgMembersWeaveRole);
          if (role === TeamMemberRole.VIEW_ONLY) {
            // Although the user may have Org Level Full Models Access they will still only be view-only if team role is view only
            if (modelsSeat !== ModelsSeatType.NOACCESS) {
              modelsSeat = ModelsSeatType.VIEWER;
            }
            if (weaveRole !== WeaveRoleType.NOACCESS) {
              weaveRole = WeaveRoleType.VIEWER;
            }
          }

          return {
            row: {
              name: member,
              email: member,
              role: member,
              action: [member, inviteKey],
              model_seat: [member, modelsSeat],
              weave_seat: [member, weaveRole],
              modelRegistryAdmin: {
                isModelRegistryAdmin:
                  entity.workflowsAdmins.find(a => a.id === member.id) != null,
                userId: member.id,
              },
            },
          };
        }),
    [
      entity.members,
      latestUniqueInvites,
      entity.workflowsAdmins,
      organization?.members,
    ]
  );

  // Edit this sort function if you add/remove columns
  const defaultSortMethod: SortFunction = useCallback((a, b, desc) => {
    let aVal = a;
    let bVal = b;

    // get proper value for 'name' column
    if (a && a.isServiceAccount != null) {
      aVal = a.text;
      bVal = b.text;
    }

    // get proper values for 'role' column
    if (a && a.role != null) {
      aVal = getRoleLabel(a);
      bVal = getRoleLabel(b);
    }

    aVal = aVal?.toLowerCase().trim();
    bVal = bVal?.toLowerCase().trim();

    // Empty cells are always at bottom of table when sorted
    if (desc) {
      if (aVal === '' && bVal !== '') {
        return -1;
      }
      if (aVal !== '' && bVal === '') {
        return 1;
      }
      if (aVal === '' && aVal === bVal) {
        return 0;
      }
    }

    // perform sort
    if (aVal > bVal) {
      return -1;
    }
    if (bVal > aVal) {
      return 1;
    }
    return 0;
  }, []);

  if (!entity) {
    return (
      <S.LoaderWrapper>
        <Loader name="team-members-table-loader" />
      </S.LoaderWrapper>
    );
  }

  const teamMembersNoDataComponent = () => {
    return (
      <Tailwind>
        <div className="text-gray-600 flex h-40 w-full items-center justify-center">
          <p className="text-lg font-semibold">No rows found</p>
        </div>
      </Tailwind>
    );
  };

  return (
    <Tailwind>
      <TypedWBReactTable<Row>
        dataTest="team-members-table"
        className="mt-8 overflow-visible border-moon-250"
        data={rows}
        columns={columns}
        searchPlaceholder={
          isWeaveAsAServiceUser ? 'Search by name, roles, teams...' : 'Search'
        }
        defaultSortMethod={defaultSortMethod}
        defaultSorted={[{id: 'username', desc: true}]}
        pageSize={20}
        getTrProps={() => ({
          className: 'flex items-center min-h-64',
        })}
        noDataComponent={teamMembersNoDataComponent}
        loading={isSettingRole}
        getSearchOptions={getSearchOptions}
      />
      <DeleteSelfRegistryAdminModal
        registryType="model"
        open={isRemoveSelfRegistryAdminModalOpen}
        onClose={() => {
          setIsRemoveSelfRegistryAdminModalOpen(false);
        }}
        username={viewer?.username ?? ''}
        entityName={entity.name}
        refetch={refetch}
      />
    </Tailwind>
  );
};

type DeleteSelfRegistryAdminModalProps = {
  registryType: string;
  open: boolean;
  username: string;
  entityName: string;
  onClose: () => void;
  refetch?: () => void;
};

const DeleteSelfRegistryAdminModal = ({
  open,
  onClose,
  username,
  entityName,
  refetch,
  registryType,
}: DeleteSelfRegistryAdminModalProps) => {
  const [deleteModelRegistryAdmin] = Generated.useDeleteWorkflowsAdminMutation({
    context: propagateErrorsContext(),
    onCompleted: refetch,
  });

  return (
    <Modal open={open} onClose={onClose}>
      <Header>
        <p>Are you sure you want to remove yourself as a registry admin?</p>
      </Header>
      <Modal.Content>
        <p>
          You are about to remove yourself as a {registryType} registry admin.
          This will revoke your privileges around adding or removing protected
          aliases as well as deleting any objects which contain protected
          aliases (such as {registryType} versions or Registered{' '}
          {capitalizeFirstLetter(registryType)}s).
        </p>
      </Modal.Content>
      <Modal.Actions>
        <Button
          size="large"
          variant="ghost"
          onClick={onClose}
          style={{marginRight: 20}}>
          Cancel
        </Button>
        <Button
          size="large"
          variant="destructive"
          style={{marginRight: 20}}
          onClick={async () => {
            await deleteModelRegistryAdmin({
              variables: {
                username,
                entityName,
                adminType: WorkflowsAdminType.Model,
              },
            });
            if (refetch != null) {
              refetch();
            }
            toast('Removed user as a model registry admin.');
            onClose();
          }}>
          Delete
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
